import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Legend, Cell
} from 'recharts';
import { Users, Tv, TrendingUp, DollarSign, Search, X } from 'lucide-react';
import { getCategories } from '../services/api';
import CategoryCard from '../components/CategoryCard';
import MetricCard from '../components/MetricCard';
import Tooltip from '../components/Tooltip';
import DataBadge from '../components/DataBadge';
import AiChat from '../components/AiChat';
import UZBEK_NAMES from '../constants/uzbekNames';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
};
const stagger = (i, base = 0) => ({ duration: 0.45, delay: base + i * 0.1, ease: 'easeOut' });

function fmt(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1e9) return (n / 1e9).toFixed(1) + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return String(n);
}

const METRIC_KEYS = {
  "Ko'rishlar": 'views',
  'Obunachilar': 'subscribers',
  'Daromad': 'revenueEstimate',
};

const CustomLineTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(10,14,26,0.92)', border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 12, padding: '10px 16px', backdropFilter: 'blur(12px)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.35)', color: '#f1f5f9', fontSize: 13,
    }}>
      <div style={{ marginBottom: 6, fontWeight: 600 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
          <span style={{ color: '#94a3b8' }}>{p.name}:</span>
          <span style={{ color: p.color, fontWeight: 600 }}>{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const CustomBarTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div style={{
      background: 'rgba(10,14,26,0.92)', border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 12, padding: '10px 16px', backdropFilter: 'blur(12px)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.35)', color: '#f1f5f9', fontSize: 13,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{d?.name}</div>
      <div>Har 1000 ko'rish: <span style={{ color: d?.color, fontWeight: 700 }}>${d?.cpmMin} — ${d?.cpmMax}</span></div>
    </div>
  );
};

export default function Dashboard() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeLine, setActiveLine] = useState("Ko'rishlar");
  const [searchQuery, setSearchQuery] = useState('');

  const load = useCallback(() => {
    setLoading(true); setError(null);
    getCategories()
      .then(d => { setCats(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <SkeletonDashboard />;
  if (error) return <ErrorCard onRetry={load} />;

  const totalSubs = cats.reduce((a, c) => a + (c.subscriberCount || 0), 0);
  const totalChannels = cats.reduce((a, c) => a + (c.channelCount || 0), 0);
  const avgGrowth = cats.length ? (cats.reduce((a, c) => a + (c.monthlyGrowth || 0), 0) / cats.length).toFixed(1) : 0;
  const avgCpm = cats.length ? (cats.reduce((a, c) => a + ((c.cpmMin + c.cpmMax) / 2 || 0), 0) / cats.length).toFixed(0) : 0;

  const lineData = (cats[0]?.trendData || []).map((d, i) => {
    const row = { month: d.month };
    cats.forEach(c => {
      if (c.trendData?.[i]) row[c.name] = c.trendData[i][METRIC_KEYS[activeLine]] || 0;
    });
    return row;
  });

  const filteredCategories = cats.filter(cat => {
    const q = searchQuery.toLowerCase();
    const uzName = (UZBEK_NAMES[cat.name] || '').toLowerCase();
    return cat.name.toLowerCase().includes(q) || uzName.includes(q);
  });

  const barData = cats.map(c => ({
    name: c.name.length > 14 ? c.name.slice(0, 13) + '…' : c.name,
    fullName: c.name,
    cpmMin: c.cpmMin,
    cpmMax: c.cpmMax,
    avg: (c.cpmMin + c.cpmMax) / 2,
    color: c.color,
  })).sort((a, b) => b.avg - a.avg);

  return (
    <div className="page">
      {/* Hero */}
      <section className="hero-premium">
        {/* Floating ambient stats */}
        <div className="fstat" style={{ top: '22%', left: '4%', animationDelay: '0s' }}>
          <div className="fstat-val">+2.4M</div>
          <div className="fstat-lbl">Ko'rish / oy</div>
        </div>
        <div className="fstat" style={{ top: '68%', left: '2%', animationDelay: '1s' }}>
          <div className="fstat-val">{cats.length || 32}</div>
          <div className="fstat-lbl">Yo'nalish</div>
        </div>
        <div className="fstat" style={{ top: '30%', right: '3%', animationDelay: '0.5s' }}>
          <div className="fstat-val">$8–22</div>
          <div className="fstat-lbl">CPM (avg)</div>
        </div>

        <motion.div className="hero-content" variants={fadeUp} initial="initial" animate="animate" transition={stagger(0)}>
          <motion.div className="hero-badge" variants={fadeUp} initial="initial" animate="animate" transition={stagger(0)}>
            <span className="hero-badge-dot"></span>
            Real-time YouTube Tahlili
          </motion.div>
          <motion.h1 className="hero-title-huge" variants={fadeUp} initial="initial" animate="animate" transition={stagger(1)}>
            Qaysi<br/>
            <span className="gradient-text-animated">yo'nalishni</span><br/>
            tanlaysiz?
          </motion.h1>
          <motion.p className="hero-subtitle-large" variants={fadeUp} initial="initial" animate="animate" transition={stagger(2)}>
            YouTube ma'lumotlari asosida eng yaxshi yo'nalishni toping
          </motion.p>
        </motion.div>

        <motion.div
          className="hero-orb-container"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: "easeOut", delay: 0.3 }}
        >
          <div className="hero-orb"></div>
        </motion.div>

        {/* Scroll hint */}
        <div className="scroll-hint">
          <span className="sh-text">SCROLL</span>
          <div className="sh-mouse"><div className="sh-wheel"></div></div>
        </div>
      </section>

      {/* Search Bar */}
      <motion.div className="search-wrap" variants={fadeUp} initial="initial" animate="animate" transition={stagger(1)}>
        <Search size={18} className="search-icon" />
        <input
          className="search-input"
          type="text"
          placeholder="Yo'nalish qidiring... (Gaming, Music, Education...)"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="search-clear" onClick={() => setSearchQuery('')}>
            <X size={16} />
          </button>
        )}
      </motion.div>

      {/* Metric Cards */}
      <motion.div
        className="metrics-row"
        variants={fadeUp} initial="initial" animate="animate" transition={stagger(2)}
      >
        <MetricCard
          icon={Users} label="Jami obunachilar" value={totalSubs} color="#378ADD"
          tooltip="Barcha kategoriyalar bo'yicha umumiy obunachi soni" index={0}
        />
        <MetricCard
          icon={Tv} label="Faol kanallar" value={totalChannels} color="#1D9E75"
          tooltip="YouTube platformasidagi faol kanallar soni" index={1}
        />
        <MetricCard
          icon={TrendingUp} label="O'rtacha o'sish" value={null} color="#D85A30"
          tooltip={`Barcha kategoriyalar bo'yicha o'rtacha oylik o'sish: +${avgGrowth}%`}
          prefix="+" suffix="%" index={2}
        />
        <MetricCard
          icon={DollarSign} label="O'rtacha CPM" value={null} color="#7F77DD"
          tooltip={`O'rtacha har 1000 ko'rish uchun taxminiy daromad: $${avgCpm}`}
          prefix="$" index={3}
        />
      </motion.div>

      {/* Category Grid */}
      <motion.section
        className="section"
        variants={fadeUp} initial="initial" whileInView="animate"
        viewport={{ once: true, amount: 0.1 }}
        transition={stagger(0, 0.1)}
      >
        <h2 className="section-title">
          {searchQuery ? `"${searchQuery}" natijalari (${filteredCategories.length})` : 'Barcha yo\'nalishlar'}
        </h2>
        {filteredCategories.length === 0 && searchQuery ? (
          <div className="search-empty anim-fadeIn">
            <span style={{ fontSize: 40 }}>🔍</span>
            <p>Hech narsa topilmadi</p>
            <span style={{ color: '#94a3b8', fontSize: 14 }}>"{searchQuery}" so'rovi bo'yicha yo'nalish mavjud emas</span>
          </div>
        ) : (
          <div className="cats-grid">
            {cats.map((cat, i) => {
              const visible = filteredCategories.includes(cat);
              return (
                <div
                  key={cat.id}
                  className={visible ? 'cat-card-filtered-in' : 'cat-card-filtered-out'}
                  style={visible ? {} : { height: 0, overflow: 'hidden', margin: 0, padding: 0 }}
                >
                  <CategoryCard cat={cat} index={i} />
                </div>
              );
            })}
          </div>
        )}
      </motion.section>

      {/* Premium Area Chart */}
      <motion.section
        className="section"
        variants={fadeUp} initial="initial" whileInView="animate"
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <div className="chart-premium">
          <div className="chart-header">
            <h2 className="section-title" style={{ margin: 0 }}>Yo'nalishlar taqqoslash</h2>
            <div className="chart-tabs">
              {Object.keys(METRIC_KEYS).map(k => (
                <button
                  key={k}
                  className={`chart-tab${activeLine === k ? ' active' : ''}`}
                  onClick={() => setActiveLine(k)}
                >{k}</button>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 24 }}>
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={lineData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <defs>
                  {cats.map(c => (
                    <linearGradient key={c.id} id={`grad-${c.id}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={c.color} stopOpacity={0.18} />
                      <stop offset="95%" stopColor={c.color} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid horizontal={true} vertical={false} stroke="rgba(148,163,184,0.08)" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={fmt} tick={{ fontSize: 11, fill: '#94a3b8' }} width={55} axisLine={false} tickLine={false} />
                <RTooltip content={<CustomLineTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 16 }} />
                {cats.map(c => (
                  <Area
                    key={c.id}
                    type="monotone"
                    dataKey={c.name}
                    stroke={c.color}
                    strokeWidth={3}
                    fill={`url(#grad-${c.id})`}
                    dot={false}
                    activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
                    isAnimationActive
                    animationDuration={1500}
                    animationEasing="ease-out"
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </motion.section>

      {/* CPM Bar Chart + Top-5 Mini Bar */}
      <motion.section
        className="section charts-two-col"
        variants={fadeUp} initial="initial" whileInView="animate"
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        {/* Horizontal CPM */}
        <div className="chart-premium">
          <h2 className="section-title" style={{ marginBottom: 20 }}>
            CPM taqqoslash <DataBadge isReal={false} label="Taxminiy CPM" />
          </h2>
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={barData} layout="vertical" margin={{ top: 5, right: 60, left: 10, bottom: 5 }}>
              <CartesianGrid horizontal={false} vertical={false} />
              <XAxis type="number" tickFormatter={v => `$${v}`} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <RTooltip content={<CustomBarTooltip />} cursor={{ fill: 'rgba(148,163,184,0.06)' }} />
              <Bar dataKey="avg" radius={[0, 8, 8, 0]} isAnimationActive animationDuration={1200} animationEasing="ease-out"
                label={{ position: 'right', formatter: (v, i) => `$${barData[i]?.cpmMin}–$${barData[i]?.cpmMax}`, fontSize: 11, fill: '#94a3b8' }}>
                {barData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top 5 Vertical Bar */}
        <div className="chart-premium">
          <h2 className="section-title" style={{ marginBottom: 20 }}>Top 5 yo'nalish</h2>
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={barData.slice(0, 5)} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid horizontal={true} vertical={false} stroke="rgba(148,163,184,0.08)" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v => `$${v}`} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <RTooltip content={<CustomBarTooltip />} cursor={{ fill: 'rgba(148,163,184,0.06)' }} />
              <Bar dataKey="avg" radius={[8, 8, 0, 0]} isAnimationActive animationDuration={1200} animationEasing="ease-out">
                {barData.slice(0, 5).map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.section>

      {/* Data quality legend */}
      <motion.div
        variants={fadeUp} initial="initial" whileInView="animate"
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        style={{
          marginTop: 40,
          padding: '14px 20px',
          borderRadius: 12,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          fontSize: 13,
          color: 'var(--text-secondary)',
          lineHeight: 1.7,
        }}
      >
        <DataBadge isReal={true} label="Live" /> YouTube API'dan real vaqtda olingan ma'lumot.{'  '}
        <DataBadge isReal={false} label="Taxminiy" /> Hisoblangan / modellashtirgan ma'lumot.{'  '}
        Trend grafiklari kunlik real ma'lumot to'plamoqda — 7+ kundan keyin Live trendga o'tadi.
      </motion.div>

      <AiChat categoryName="YouTube" color="#378ADD" />
    </div>
  );
}

function SkeletonDashboard() {
  return (
    <div className="page">
      <section className="hero">
        <div className="skeleton" style={{ height: 48, width: 380, borderRadius: 12, marginBottom: 16 }} />
        <div className="skeleton" style={{ height: 22, width: 260, borderRadius: 8 }} />
      </section>
      <div className="metrics-row">
        {[...Array(4)].map((_, i) => <div key={i} className="skeleton metric-card-skeleton" />)}
      </div>
      <div className="cats-grid" style={{ marginTop: 32 }}>
        {[...Array(6)].map((_, i) => <div key={i} className="skeleton cat-card-skeleton" />)}
      </div>
    </div>
  );
}

function ErrorCard({ onRetry }) {
  return (
    <div className="page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div className="error-card">
        <span style={{ fontSize: 48 }}>⚠️</span>
        <h2>Ma'lumot yuklanmadi</h2>
        <p>Backend serveriga ulanishda xatolik yuz berdi. Server ishga tushirilganligini tekshiring.</p>
        <button className="retry-btn" onClick={onRetry}>Qayta urinib ko'ring</button>
      </div>
    </div>
  );
}
